import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface MachineOptions {
  reducedMotion: boolean;
  coarsePointer: boolean;
}

interface MachineParticleSystem {
  setActive(active: boolean): void;
}

type MachinePhase =
  | 'opening'
  | 'perception'
  | 'compute'
  | 'powertrain'
  | 'chassis'
  | 'fleet'
  | 'exit';

const select = <T extends Element>(root: ParentNode, selector: string): T | null =>
  root.querySelector<T>(selector);

const selectAll = <T extends Element>(root: ParentNode, selector: string): T[] =>
  Array.from(root.querySelectorAll<T>(selector));

function resolvePhase(progress: number, isMobile: boolean): MachinePhase {
  if (progress < 0.18) return 'opening';
  if (progress < 0.35) return 'perception';
  if (progress < 0.5) return 'compute';
  if (progress < (isMobile ? 0.82 : 0.7)) return 'powertrain';
  if (progress < 0.82) return 'chassis';
  if (progress < 0.94) return 'fleet';
  return 'exit';
}

function setPhaseRail(section: HTMLElement, phase: MachinePhase): void {
  const order: MachinePhase[] = ['perception', 'compute', 'powertrain', 'chassis'];
  const currentIndex = order.indexOf(phase);
  selectAll<HTMLElement>(section, '[data-machine-step]').forEach((step, index) => {
    step.classList.toggle('is-active', currentIndex >= index || phase === 'fleet' || phase === 'exit');
  });
}

function preloadMachineAssets(section: HTMLElement): void {
  const images = selectAll<HTMLImageElement>(section, '.machine-visual img');
  const load = (): void => {
    images.forEach((image) => {
      image.loading = 'eager';
      void image.decode().catch(() => undefined);
    });
  };

  if (!('IntersectionObserver' in window)) {
    load();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      load();
      observer.disconnect();
    },
    { rootMargin: '170% 0px' },
  );
  observer.observe(section);
}

function initSensorInspection(section: HTMLElement): void {
  const hotspots = selectAll<HTMLButtonElement>(section, '.machine-hotspot');
  const brackets = selectAll<SVGGElement>(section, '.sensor-bracket');
  hotspots.forEach((hotspot) => {
    const activate = (): void => {
      const sensor = hotspot.dataset.sensor ?? '';
      const activeBracket = select<SVGGElement>(section, `.sensor-bracket-${sensor}`);
      section.dataset.activeSensor = sensor;
      section.classList.add('has-sensor-focus');
      hotspot.classList.add('is-active');
      gsap.to(brackets, { opacity: 0.16, duration: 0.32, ease: 'power2.out', overwrite: 'auto' });
      if (activeBracket) {
        gsap.to(activeBracket, { opacity: 1, duration: 0.32, ease: 'power2.out', overwrite: 'auto' });
      }
    };
    const deactivate = (): void => {
      delete section.dataset.activeSensor;
      section.classList.remove('has-sensor-focus');
      hotspot.classList.remove('is-active');
      gsap.to(brackets, { opacity: 0.56, duration: 0.32, ease: 'power2.out', overwrite: 'auto' });
    };

    hotspot.addEventListener('pointerenter', activate);
    hotspot.addEventListener('pointerleave', deactivate);
    hotspot.addEventListener('focus', activate);
    hotspot.addEventListener('blur', deactivate);
  });
}

function initStudioParallax(section: HTMLElement, environment: HTMLElement): void {
  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let active = false;

  section.addEventListener('pointermove', (event) => {
    const rect = section.getBoundingClientRect();
    targetX = (event.clientX - rect.left) / Math.max(1, rect.width) - 0.5;
    targetY = event.clientY / window.innerHeight - 0.5;
  });
  section.addEventListener('pointerenter', () => { active = true; });
  section.addEventListener('pointerleave', () => {
    active = false;
    targetX = 0;
    targetY = 0;
  });

  gsap.ticker.add(() => {
    currentX += (targetX - currentX) * (active ? 0.028 : 0.05);
    currentY += (targetY - currentY) * (active ? 0.028 : 0.05);
    gsap.set(environment, { x: currentX * 7, y: currentY * 5 });
  });
}

function initMachineParticles(canvas: HTMLCanvasElement): MachineParticleSystem {
  const context = canvas.getContext('2d');
  if (!context) return { setActive: () => undefined };

  const count = 22;
  let width = window.innerWidth;
  let height = window.innerHeight;
  let active = false;
  let frame = 0;
  const particles = Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random(),
    radius: 0.3 + Math.random() * 0.65,
    alpha: 0.035 + Math.random() * 0.075,
    drift: 0.00002 + Math.random() * 0.000045,
    phase: Math.random() * Math.PI * 2,
  }));

  const resize = (): void => {
    width = window.innerWidth;
    height = window.innerHeight;
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  const render = (time: number): void => {
    if (!active) {
      frame = 0;
      return;
    }
    context.clearRect(0, 0, width, height);
    particles.forEach((particle) => {
      const x = (particle.x + Math.sin(time * particle.drift + particle.phase) * 0.012) * width;
      const y = (particle.y - (time * particle.drift * 0.08) % 1) * height;
      context.beginPath();
      context.arc(x, y < 0 ? y + height : y, particle.radius, 0, Math.PI * 2);
      context.fillStyle = `rgba(205,218,224,${particle.alpha})`;
      context.fill();
    });
    frame = requestAnimationFrame(render);
  };

  resize();
  window.addEventListener('resize', resize, { passive: true });

  return {
    setActive(nextActive: boolean): void {
      if (active === nextActive) return;
      active = nextActive;
      if (active && frame === 0) frame = requestAnimationFrame(render);
      if (!active && frame !== 0) {
        cancelAnimationFrame(frame);
        frame = 0;
        context.clearRect(0, 0, width, height);
      }
    },
  };
}

export function initIntelligenceOnWheels(options: MachineOptions): void {
  const section = document.querySelector<HTMLElement>('#intelligence-on-wheels');
  if (!section) return;

  const stage = select<HTMLElement>(section, '#machine-stage');
  const particleCanvas = select<HTMLCanvasElement>(section, '#machine-particle-field');
  const environment = select<HTMLElement>(section, '.machine-environment');
  const entryLine = select<HTMLElement>(section, '#machine-entry-line');
  const entryLineShape = select<HTMLElement>(section, '#machine-entry-line span');
  const master = select<HTMLElement>(section, '#machine-master');
  const headlight = select<HTMLElement>(section, '#machine-headlight');
  const cockpit = select<HTMLElement>(section, '#machine-cockpit');
  const cutaway = select<HTMLElement>(section, '#machine-cutaway');
  const chassis = select<HTMLElement>(section, '#machine-chassis');
  const ledReflection = select<HTMLElement>(section, '#machine-led-reflection');
  const lightSweep = select<HTMLElement>(section, '#machine-light-sweep');
  const perceptionOverlay = select<SVGSVGElement>(section, '#machine-perception-overlay');
  const computeOverlay = select<SVGSVGElement>(section, '#machine-compute-overlay');
  const powerOverlay = select<SVGSVGElement>(section, '#machine-power-overlay');
  const chassisOverlay = select<SVGSVGElement>(section, '#machine-chassis-overlay');
  const systemOverlay = select<SVGSVGElement>(section, '#machine-system-overlay');
  const intro = select<HTMLElement>(section, '#machine-intro');
  const kicker = select<HTMLElement>(section, '.machine-kicker');
  const titleLines = selectAll<HTMLElement>(section, '.machine-title-mask > span');
  const introCopy = select<HTMLElement>(section, '.machine-intro-copy');
  const perceptionCopy = select<HTMLElement>(section, '#system-perception');
  const computeCopy = select<HTMLElement>(section, '#system-compute');
  const powerCopy = select<HTMLElement>(section, '#system-powertrain');
  const chassisCopy = select<HTMLElement>(section, '#system-chassis');
  const telemetry = select<HTMLElement>(section, '#machine-telemetry');
  const hotspots = select<HTMLElement>(section, '#machine-hotspots');
  const systemLabels = select<HTMLElement>(section, '#machine-system-labels');
  const fleetStatement = select<HTMLElement>(section, '#machine-fleet-statement');
  const statementLines = selectAll<HTMLElement>(section, '.machine-statement-mask > span');
  const fleetCopy = select<HTMLElement>(section, '.machine-fleet-copy');
  const dataStream = select<HTMLElement>(section, '#machine-data-stream');
  const dataFragments = selectAll<HTMLElement>(section, '#machine-data-stream span');
  const phaseRail = select<HTMLElement>(section, '#machine-phase-rail');
  const frameMeta = select<HTMLElement>(section, '.machine-frame-meta');
  const vignette = select<HTMLElement>(section, '#machine-vignette');
  const exitBlackout = select<HTMLElement>(section, '#machine-exit-blackout');
  const exitLine = select<HTMLElement>(section, '#machine-exit-line');
  const exitLineShape = select<HTMLElement>(section, '#machine-exit-line span');

  if (
    !stage || !particleCanvas || !environment || !entryLine || !entryLineShape || !master || !headlight ||
    !cockpit || !cutaway || !chassis || !ledReflection || !lightSweep ||
    !perceptionOverlay || !computeOverlay || !powerOverlay || !chassisOverlay ||
    !systemOverlay || !intro || !kicker || titleLines.length !== 2 || !introCopy ||
    !perceptionCopy || !computeCopy || !powerCopy || !chassisCopy || !telemetry ||
    !hotspots || !systemLabels || !fleetStatement || statementLines.length !== 2 ||
    !fleetCopy || !dataStream || !phaseRail || !frameMeta || !vignette ||
    !exitBlackout || !exitLine || !exitLineShape
  ) return;

  preloadMachineAssets(section);
  const isMobile = window.innerWidth <= 760;

  if (options.reducedMotion) {
    stage.style.visibility = 'visible';
    section.dataset.machinePhase = 'fleet';
    section.dataset.storyProgress = '1.0000';
    return;
  }

  const systemCopies = [perceptionCopy, computeCopy, powerCopy, chassisCopy];
  const engineeringLayers = [perceptionOverlay, computeOverlay, powerOverlay, chassisOverlay, systemOverlay];
  const visualLayers = [master, headlight, cockpit, cutaway, chassis];

  gsap.set(stage, { visibility: 'hidden' });
  gsap.set(environment, { opacity: 1 });
  gsap.set(entryLine, { opacity: 1 });
  gsap.set(entryLineShape, { width: '76vw', y: 0, rotate: -6, scaleY: 0.82 });
  gsap.set(visualLayers, { opacity: 0 });
  gsap.set(master, { scale: 1.62, xPercent: 5, transformOrigin: '64% 58%', filter: 'brightness(.34)' });
  gsap.set(headlight, { scale: 1.06, transformOrigin: '64% 50%', filter: 'brightness(.38)' });
  gsap.set(cockpit, {
    scale: 1.08,
    transformOrigin: '52% 48%',
    filter: 'brightness(.4)',
    clipPath: 'polygon(42% 34%, 52% 34%, 52% 42%, 42% 42%)',
  });
  gsap.set(cutaway, {
    scale: 1.07,
    xPercent: 2,
    transformOrigin: '61% 61%',
    filter: 'brightness(.44)',
    clipPath: 'inset(43% 40% 35% 47%)',
  });
  gsap.set(chassis, { scale: 1.06, xPercent: 2, transformOrigin: '58% 63%', filter: 'brightness(.46)' });
  gsap.set(ledReflection, { opacity: 0, scaleX: 0.55, rotate: -6 });
  gsap.set(lightSweep, { opacity: 0, xPercent: 0 });
  gsap.set(engineeringLayers, { opacity: 0 });
  gsap.set(intro, { opacity: 1 });
  gsap.set(kicker, { opacity: 0, y: 10, letterSpacing: '.55em' });
  gsap.set(titleLines, { yPercent: 140 });
  gsap.set(introCopy, { opacity: 0, y: 10, filter: 'blur(4px)' });
  gsap.set(systemCopies, { opacity: 0, x: -16, filter: 'blur(4px)' });
  gsap.set(telemetry, { opacity: 0, x: 14, filter: 'blur(3px)' });
  gsap.set(hotspots, { opacity: 0 });
  gsap.set(systemLabels, { opacity: 0 });
  gsap.set(fleetStatement, { opacity: 0 });
  gsap.set(statementLines, { yPercent: 140 });
  gsap.set(fleetCopy, { opacity: 0, y: 9 });
  gsap.set(dataStream, { opacity: 0 });
  gsap.set(dataFragments, { x: -16 });
  gsap.set(phaseRail, { opacity: 0, y: 8 });
  gsap.set(frameMeta, { opacity: 0 });
  gsap.set(vignette, { opacity: 0.82 });
  gsap.set(exitBlackout, { opacity: 0 });
  gsap.set(exitLine, { opacity: 0 });
  gsap.set(exitLineShape, { width: 7, scaleY: 0.75 });

  initSensorInspection(section);
  if (!options.coarsePointer && window.innerWidth > 760) initStudioParallax(section, environment);
  const particleSystem = isMobile ? { setActive: () => undefined } : initMachineParticles(particleCanvas);

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 2.8 : 3.2))}`,
      scrub: isMobile ? 0.42 : 0.62,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => {
        gsap.set(stage, { visibility: 'visible' });
        particleSystem.setActive(true);
      },
      onEnterBack: () => {
        gsap.set(stage, { visibility: 'visible' });
        particleSystem.setActive(true);
      },
      onLeave: () => { particleSystem.setActive(false); },
      onLeaveBack: () => {
        gsap.set(stage, { visibility: 'hidden' });
        particleSystem.setActive(false);
      },
      onUpdate: (self) => {
        const phase = resolvePhase(self.progress, isMobile);
        section.dataset.storyProgress = self.progress.toFixed(4);
        if (section.dataset.machinePhase !== phase) {
          section.dataset.machinePhase = phase;
          setPhaseRail(section, phase);
        }
      },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) gsap.set(stage, { visibility: 'visible' });
      },
    },
  });

  timeline
    // Network pulse -> physical LED -> headlight macro.
    .to(entryLineShape, { y: '-14.5vh', width: '54vw', rotate: 5.2, scaleY: 1, duration: 0.055, ease: 'power2.inOut' }, 0)
    .to(ledReflection, { opacity: 0.62, scaleX: 1, rotate: 5.2, duration: 0.055 }, 0.012)
    .to(headlight, { opacity: 1, filter: 'brightness(.9)', duration: 0.06, ease: 'power2.out' }, 0.018)
    .to(entryLine, { opacity: 0, duration: 0.035 }, 0.047)
    .to(headlight, { scale: 1, filter: 'brightness(1)', duration: 0.07, ease: 'power2.out' }, 0.035)
    .to(ledReflection, { opacity: 0, duration: 0.035 }, 0.066)

    // Pull back into the complete machine and reveal the editorial title.
    .to(master, { opacity: 1, filter: 'brightness(.86)', duration: 0.055 }, 0.092)
    .to(headlight, { opacity: 0, scale: 0.99, duration: 0.05 }, 0.098)
    .to(master, { scale: 1, xPercent: 3, filter: 'brightness(1)', duration: 0.085, ease: 'power3.out' }, 0.095)
    .to(lightSweep, { opacity: 0.7, duration: 0.02 }, 0.102)
    .to(lightSweep, { xPercent: 1450, duration: 0.075, ease: 'power2.inOut' }, 0.105)
    .to(lightSweep, { opacity: 0, duration: 0.022 }, 0.166)
    .to(kicker, { opacity: 1, y: 0, letterSpacing: '.32em', duration: 0.035, ease: 'power3.out' }, 0.108)
    .to(titleLines, { yPercent: 0, duration: 0.052, stagger: 0.008, ease: 'power3.out' }, 0.116)
    .to(introCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.04, ease: 'power3.out' }, 0.145)
    .to(intro, { opacity: 1, duration: 0.025 }, 0.16)

    // 01 / Perception — camera returns to the optical hardware and holds.
    .to(intro, { opacity: 0, x: -18, y: -10, duration: 0.035 }, 0.18)
    .to(master, { opacity: 0.42, scale: 1.05, duration: 0.04 }, 0.185)
    .to(headlight, { opacity: 1, scale: 1, filter: 'brightness(.82)', duration: 0.045 }, 0.19)
    .to(perceptionOverlay, { opacity: 0.74, duration: 0.045 }, 0.198)
    .to(hotspots, { opacity: 1, duration: 0.035 }, 0.21)
    .to(perceptionCopy, { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.04, ease: 'power3.out' }, 0.205)
    .to(phaseRail, { opacity: 1, y: 0, duration: 0.035 }, 0.205)
    .to(frameMeta, { opacity: 0.65, duration: 0.035 }, 0.215)
    .to(perceptionOverlay, { opacity: 0.86, duration: 0.035 }, 0.255)
    .to(perceptionCopy, { opacity: 1, duration: 0.055 }, 0.25)
    .to([perceptionCopy, hotspots], { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.03 }, 0.32)
    .to(perceptionOverlay, { opacity: 0, duration: 0.035 }, 0.325)

    // 02 / Connected compute — pass through the windshield into the cab architecture.
    .to(headlight, { opacity: 0, scale: 1.025, duration: 0.035 }, 0.338)
    .to(master, { opacity: 0.72, scale: 1.02, xPercent: 5, filter: 'brightness(.68)', duration: 0.035 }, 0.34)
    .to(cockpit, {
      opacity: 0.44,
      scale: 1,
      filter: 'brightness(.82)',
      clipPath: 'polygon(27% 20%, 65% 20%, 64% 49%, 27% 49%)',
      duration: 0.055,
      ease: 'power2.out',
    }, 0.345)
    .to(computeOverlay, { opacity: 0.68, duration: 0.045 }, 0.36)
    .to(computeCopy, { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.04, ease: 'power3.out' }, 0.365)
    .to(telemetry, { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.042, ease: 'power3.out' }, 0.385)
    .to('.data-traces path', { strokeDashoffset: -72, duration: 0.1, ease: 'none' }, 0.38)
    .to(computeCopy, { opacity: 1, duration: 0.055 }, 0.405)
    .to([computeCopy, telemetry], { opacity: 0, x: -10, filter: 'blur(3px)', duration: 0.03 }, 0.475)
    .to(computeOverlay, { opacity: 0, duration: 0.03 }, 0.48)

    // 03 / Powertrain — localized smoked cutaway and physically directed energy flow.
    .to(cockpit, { opacity: 0, scale: 1.03, duration: 0.035 }, 0.49)
    .to(master, { opacity: 0.56, scale: 1.015, xPercent: 4, filter: 'brightness(.72)', duration: 0.045 }, 0.49)
    .to(cutaway, {
      opacity: 0.68,
      scale: 1,
      xPercent: 2,
      filter: 'brightness(.86)',
      clipPath: 'inset(.01%)',
      duration: 0.055,
      ease: 'power2.out',
    }, 0.495)
    .to(powerOverlay, { opacity: 0.82, duration: 0.045 }, 0.515)
    .to(powerCopy, { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.045, ease: 'power3.out' }, 0.525)
    .to('.power-line', { strokeDashoffset: -90, duration: 0.14, ease: 'none' }, 0.53)
    .to(powerCopy, { opacity: 1, duration: 0.075 }, 0.57)
    .to(powerOverlay, { opacity: 0.92, duration: 0.05 }, 0.585)
    .to(powerCopy, { opacity: isMobile ? 1 : 0, x: isMobile ? 0 : -12, filter: isMobile ? 'blur(0px)' : 'blur(3px)', duration: 0.032 }, 0.67)
    .to(powerOverlay, { opacity: isMobile ? 0.82 : 0, duration: 0.035 }, 0.675)

    // 04 / Chassis — a macro contact-point study.
    .to(cutaway, { opacity: isMobile ? 0.68 : 0, scale: isMobile ? 1 : 1.025, duration: 0.035 }, 0.69)
    .to(chassis, { opacity: isMobile ? 0 : 0.96, scale: 1, xPercent: 2, filter: 'brightness(.94)', duration: 0.047, ease: 'power2.out' }, 0.695)
    .to(chassisOverlay, { opacity: isMobile ? 0 : 0.78, duration: 0.04 }, 0.71)
    .to(chassisCopy, { opacity: isMobile ? 0 : 1, x: 0, filter: 'blur(0px)', duration: 0.04, ease: 'power3.out' }, 0.715)
    .to('.wheel-target circle:last-of-type', { rotation: 2, transformOrigin: 'center', duration: 0.09, ease: 'none' }, 0.72)
    .to(chassisCopy, { opacity: 1, duration: 0.042 }, 0.75)
    .to(isMobile ? powerCopy : chassisCopy, { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.027 }, 0.795)
    .to(isMobile ? powerOverlay : chassisOverlay, { opacity: 0, duration: 0.028 }, 0.797)

    // Whole system — all technical domains resolve onto one heavy vehicle.
    .to(isMobile ? cutaway : chassis, { opacity: 0, scale: 1.03, duration: 0.035 }, 0.805)
    .to(master, { opacity: 0.9, scale: 1, xPercent: isMobile ? 10 : 16, filter: 'brightness(.76)', duration: 0.055, ease: 'power2.out' }, 0.81)
    .to(systemOverlay, { opacity: 0.68, duration: 0.04 }, 0.822)
    .to(systemLabels, { opacity: 1, duration: 0.04 }, 0.828)
    .to(fleetStatement, { opacity: 1, duration: 0.025 }, 0.83)
    .to(statementLines, { yPercent: 0, duration: 0.05, stagger: 0.008, ease: 'power3.out' }, 0.835)
    .to(fleetCopy, { opacity: 1, y: 0, duration: 0.035, ease: 'power3.out' }, 0.862)
    .to(dataStream, { opacity: 1, duration: 0.03 }, 0.84)
    .to(dataFragments, { x: 22, duration: 0.1, stagger: 0.006, ease: 'none' }, 0.84)
    .to(fleetStatement, { opacity: 1, duration: 0.045 }, 0.87)

    // Precision line handoff for the future warehouse sequence.
    .to([fleetStatement, systemLabels, systemOverlay, dataStream, phaseRail, frameMeta], { opacity: 0, y: -8, duration: 0.032 }, 0.925)
    .to(master, { opacity: 0.08, scale: 1.13, xPercent: -8, filter: 'brightness(.28)', duration: 0.06 }, 0.93)
    .to(vignette, { opacity: 1, duration: 0.045 }, 0.93)
    .to(exitBlackout, { opacity: 1, duration: 0.045 }, 0.94)
    .to(exitLine, { opacity: 1, duration: 0.025 }, 0.958)
    .to(exitLineShape, { width: '82vw', scaleY: 1, duration: 0.042, ease: 'power2.inOut' }, 0.958);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
